import { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import chatStore from 'shared/store/chatStore';

import { CONSTRAINTS } from '../constants';
import { useToast } from 'shared/components/ui/use-toast';
import activityPanelStore from 'shared/store/activityPanelStore';
import appStatusStore from 'shared/store/appStatusStore';
import constraintsStore from 'shared/store/constraintsStore';
import { ProductDisplayRequestItem } from 'types/ProductTypes';
import { getPortfolioData } from 'utils/portfolioUtils';
import { clearAgentChangeIndicators } from '../utils/clearAgentChangeIndicators';
import { buildConstraintsSyncPayload } from '../lifecycle/buildConstraintsSyncPayload';
import { buildVincoliRecap } from '../lifecycle/buildVincoliRecap';
import { renderVincoliRecap } from '../lifecycle/renderVincoliRecap';
import { sectionLabel } from '../lifecycle/vincoliRecapLabels';

export const useConstraintsPanel = () => {
  const {
    setUserSentFirstMessage,
    fetchPtfByPortfolioData,
    finecoPortfolioArtifacts,
  } = activityPanelStore();

  const constraintsPanelMode = activityPanelStore((state) => state.constraintsPanelMode);

  const { setAppStatus } = appStatusStore();
  const { setPendingConstraintsMetadata, conversationFlowLoading, threadMetadata } =
    chatStore();

  // The SDK's conversationFlowLoading flips true for ANY thread operation —
  // including our silent `seedThread` metadata sync. Exclude that case so a
  // Reset/Apply doesn't dim the panel or show the busy overlay; only real
  // agent runs do. `syncInFlight` is true for the duration of any
  // FE-initiated write (seedThread / continueThread / createThreadAndStart)
  // — exactly the window during which conversationFlowLoading is true but
  // the user did NOT trigger an agent run.
  const syncInFlight = constraintsStore((s) => s.syncInFlight);
  const runInProgress = conversationFlowLoading && !syncInFlight;

  const [openedConstraints, setOpenedConstraints] = useState<string[]>([]);
  const loc = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation();
  const threadId = useParams().threadId;
  const contentRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Invia is enabled whenever there is a working document.
  // This covers: fresh edits (isModified), post-Reset re-send, and advisor
  // confirming constraints that happen to match the baseline unchanged.
  const enableSubmitButton = constraintsStore((s) => s.workingDocument !== null);

  // Reset opened constraints on route change
  useEffect(() => {
    setOpenedConstraints([]);
  }, [loc.pathname]);

  // Load constraints and fetch portfolio data on threadId change
  useEffect(() => {
    if (threadId) {
      const storedConstraints = activityPanelStore.getState().getOpenedConstraints(threadId);
      setOpenedConstraints(storedConstraints);

      // Prefer v2 API with portfolio data, fallback to v1 with thread ID
      const threadArtifacts = finecoPortfolioArtifacts[threadId];
      const latestArtifact = threadArtifacts?.[threadArtifacts.length - 1];
      const portfolioData = latestArtifact ? getPortfolioData(latestArtifact) : null;

      if (portfolioData) {
        fetchPtfByPortfolioData(portfolioData);
      }

      // Metadata will be loaded via real-time events
    }
  }, [threadId, finecoPortfolioArtifacts]);

  // Note: Thread metadata changes are handled by useConstraintsChangeDetection hook
  // which calls setPreviousConstraintsStore and fetches product display data.
  // This avoids duplicate calls that would reset persistentConstraintsState twice.

  // Sync portfolio_product_values from metadata to store
  useEffect(() => {
    if (threadMetadata && threadMetadata[CONSTRAINTS.PORTFOLIO_PRODUCT_VALUES_KEY]) {
      activityPanelStore.getState().setPortfolioProductValues(threadMetadata[CONSTRAINTS.PORTFOLIO_PRODUCT_VALUES_KEY]);
    }
  }, [threadMetadata]);

  const handleReset = () => {
    const workingBefore = constraintsStore.getState().workingDocument;
    const baseline = constraintsStore.getState().baselineDocument();
    const modeAtReset = constraintsStore.getState().mode;
    const appliedProfileAtReset = constraintsStore.getState().appliedProfile;
    const productListsAtReset = activityPanelStore.getState().productLists;
    const microcategoryNamesAtReset = activityPanelStore.getState().microcategoryNames;
    const getProductDisplayAtReset = activityPanelStore.getState().getProductDisplay;
    const importedPortfolioAtReset = chatStore.getState().threadMetadata?.[
      CONSTRAINTS.IMPORTED_PORTFOLIO_KEY
    ] as Record<string, any> | undefined;
    const importedPortfolioProductsAtReset = (importedPortfolioAtReset?.portfolio_products
      ?? null) as Record<string, { hold_mode?: string | null }> | null;

    const preResetRecap = buildVincoliRecap(
      workingBefore ?? {},
      baseline ?? {},
      modeAtReset,
      appliedProfileAtReset,
      {
        t,
        getProductDisplay: getProductDisplayAtReset,
        productLists: productListsAtReset,
        microcategoryNames: microcategoryNamesAtReset,
        importedPortfolioProducts: importedPortfolioProductsAtReset,
      },
    );
    const name = preResetRecap.configurationName;
    // Count what Reset will ACTUALLY revert: every recap bullet EXCEPT the
    // ones in sections Reset deliberately preserves. `preservePortfolioFields`
    // keeps only the product subtree across Reset / Predefiniti / profile
    // switch — liquidity used to be preserved in rebalancing but the carry-
    // over was a client-flagged bug (Apporto / Prelievo entries survived a
    // Reset despite never being saved), so liquidity now resets and the
    // toast counts it.
    const preservedSectionHeadings = new Set<string>([sectionLabel('product')]);
    const countBullets = (bullets: { children?: any[] }[]): number =>
      bullets.reduce((acc, b) => acc + 1 + countBullets(b.children ?? []), 0);
    const count = preResetRecap.sections
      .filter((s) => !preservedSectionHeadings.has(s.heading))
      .reduce(
        (acc, s) =>
          acc
          + countBullets(s.bullets)
          + (s.subSections ?? []).reduce((a, sub) => a + countBullets(sub.bullets), 0),
        0,
      );

    constraintsStore.getState().reset();
    // Reset snaps the working document back to the baseline; any red dot or
    // orange "the agent changed this" decoration from a prior agent turn is
    // no longer meaningful.
    clearAgentChangeIndicators();

    // Title depends on what Reset snapped back to: a named saved profile, or
    // the default ("Vincoli Predefiniti") baseline. Italian gender agreement
    // differs between the two ("configurazione … applicata" vs "vincoli
    // predefiniti applicati"), so they are separate strings, not one {name}
    // template. The body is constant; the empty variant fires when Reset
    // reverted nothing.
    toast({
      title: appliedProfileAtReset
        ? t('constraints.resetToast.titleProfile', { name })
        : t('constraints.resetToast.titleDefault'),
      description: count > 0
        ? t('constraints.resetToast.body')
        : t('constraints.resetToast.bodyEmpty'),
    });
  };

  const toggleConstraint = (type: string) => {
    setOpenedConstraints((prev) => {
      const newConstraints = prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type];

      // Opening any section is the user's "I've seen the agent's changes
      // in this section" signal — clear that section's red dot via
      // acknowledgedSectionGroups (in-panel orange borders survive
      // until the user types each field). Closing does NOT acknowledge.
      if (!prev.includes(type)) {
        constraintsStore.getState().acknowledgeSection([type]);
      }
      activityPanelStore.getState().setOpenedConstraints(threadId || '', newConstraints);

      return newConstraints;
    });
  };

  const saveConstraints = async () => {
    setUserSentFirstMessage(true);

    const workingDocument = constraintsStore.getState().workingDocument;

    // Fetch display data for any products in the saved constraints
    const productData = workingDocument?.product as Record<string, any> | null | undefined;
    if (productData) {
      const allItems: ProductDisplayRequestItem[] = [
        ...Object.keys(productData.individual_products || {}).map((isin) => ({
          isin,
          investment_type: null as string | null,
        })),
        ...Object.keys(productData.list_products || {}).map((isin) => ({
          isin,
          investment_type: null as string | null,
        })),
        ...Object.keys(productData.portfolio_products || {}).map((isin) => ({
          isin,
          investment_type: productData.portfolio_products?.[isin]?.investment_type ?? null,
        })),
      ];
      if (allItems.length > 0) {
        activityPanelStore.getState().fetchProductDisplayData(allItems);
      }
    }

    // Build via the shared helper so Invia, silent sync, and the chat-input
    // path all produce identical metadata. MODE_KEY is added by
    // useConstraintsMetadata when this becomes a new-thread create.
    const { metadata } = buildConstraintsSyncPayload();

    const baseline = constraintsStore.getState().baselineDocument();
    const mode = constraintsStore.getState().mode;
    const appliedProfile = constraintsStore.getState().appliedProfile;
    const productLists = activityPanelStore.getState().productLists;
    const microcategoryNames = activityPanelStore.getState().microcategoryNames;
    const getProductDisplay = activityPanelStore.getState().getProductDisplay;
    const importedPortfolio = chatStore.getState().threadMetadata?.[
      CONSTRAINTS.IMPORTED_PORTFOLIO_KEY
    ] as Record<string, any> | undefined;
    const importedPortfolioProducts = (importedPortfolio?.portfolio_products
      ?? null) as Record<string, { hold_mode?: string | null }> | null;

    const recap = buildVincoliRecap(
      workingDocument ?? {},
      baseline ?? {},
      mode,
      appliedProfile,
      {
        t,
        getProductDisplay,
        productLists,
        microcategoryNames,
        importedPortfolioProducts,
      },
    );
    const recapText = renderVincoliRecap(recap, t);

    setPendingConstraintsMetadata({ metadata, recapText });

    // Invia sends the user's constraints to the agent — that's an
    // explicit acknowledgement of every prior agent change. Clear the
    // indicators so a subsequent agent response with the SAME values
    // does not light the dot (the agent affirming what the user just
    // sent is not news).
    clearAgentChangeIndicators();

    toast({
      title: t(CONSTRAINTS.TOAST.TITLE_KEY),
      description: t(CONSTRAINTS.TOAST.DESCRIPTION_KEY),
    });
    setAppStatus('idle');
  };

  const handleSubmit = () => saveConstraints();

  return {
    // State
    openedConstraints,
    enableSubmitButton,
    contentRefs,
    runInProgress,

    // Store data
    constraintsPanelMode,

    // Actions
    handleReset,
    handleSubmit,
    toggleConstraint,
    saveConstraints,
  };
};
